import { GuestTagList } from 'restaurantAdmin/components/guestTags/GuestTagList/GuestTagList';
import { GuestTagPill } from 'restaurantAdmin/components/guestTags/GuestTagPill/GuestTagPill';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  RESERVATIONS_OCCUPANTS_PAGE_PATH,
  RESERVATIONS_SERVICE_PATH,
} from 'restaurantAdmin/paths';
import { Dialog } from '@base-ui/react';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { Icon } from '@components/icon/Icon';
import { LoadingPage } from '@components/LoadingPage';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import {
  toISODateFormat,
  toShortDateFormat,
} from '@shared/utils/dateFormatters';
import { ISOTimeTo12HourTime } from '@shared/utils/time';
import { useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import typography from '~styles/typography.scss';
import {
  deleteRestaurantNote,
  getRestaurantNote,
  RestaurantDayNote,
  saveRestaurantNote,
} from '../service/apiHelpers';
import {
  isReservation,
  ServiceOccupant,
  ServiceReservation,
} from './apiHelpers';
import {
  DRAWER_PARAM,
  OCCUPANT_DETAILS,
  OCCUPANT_ID_PARAM,
  OCCUPANT_TYPE_PARAM,
} from '../../utils/constants';
import styles from './ServiceOverviewDrawer.scss';
import { useReservationServiceContext } from './state/ReservationServiceContext';
import { useOccupantsQuery } from './hooks/useOccupantsQuery';

export enum Tab {
  NOTABLE_PARTIES = 'notableParties',
  NOTES = 'notes',
}

export const ServiceOverviewDrawer = ({
  defaultTab = Tab.NOTABLE_PARTIES,
}: {
  defaultTab?: Tab;
}) => {
  const { selectedDate } = useReservationServiceContext();
  const formattedDate = toShortDateFormat(selectedDate);
  const [tab, setTab] = useState(defaultTab);

  return (
    <div
      className={styles.container}
      aria-label="Service Details Drawer"
      role="dialog"
    >
      <header className={styles.headerBar}>
        <div className={styles.title}>
          <Icon name="note" className={styles.icon} />
          <h4 className={cx(typography.h4)}>Overview for {formattedDate}</h4>
        </div>

        <Dialog.Close
          render={({ onClick }) => (
            <Button
              label="Close"
              onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
              variant={ButtonVariants.Tertiary}
            />
          )}
        />
      </header>

      <div className={styles.content}>
        <div role="tablist" className={styles.tabBar}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === Tab.NOTABLE_PARTIES}
            onClick={() => setTab(Tab.NOTABLE_PARTIES)}
            className={cx({
              [styles.tab]: true,
              [styles.activeTab]: tab === Tab.NOTABLE_PARTIES,
            })}
          >
            Notable Parties
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={tab === Tab.NOTES}
            onClick={() => setTab(Tab.NOTES)}
            className={cx(styles.tab, {
              [styles.activeTab]: tab === Tab.NOTES,
            })}
          >
            Notes
          </button>
        </div>

        {tab === Tab.NOTABLE_PARTIES && <NotableParties />}
        {tab === Tab.NOTES && <RestaurantNotes date={selectedDate} />}
      </div>
    </div>
  );
};

const occupantDetailsHref = (occupantId: string): string => {
  const queryParams = new URLSearchParams();
  queryParams.set(DRAWER_PARAM, OCCUPANT_DETAILS);
  queryParams.set(OCCUPANT_ID_PARAM, occupantId);
  queryParams.set(OCCUPANT_TYPE_PARAM, 'reservation');

  return `${RESERVATIONS_SERVICE_PATH}/${RESERVATIONS_OCCUPANTS_PAGE_PATH}?${queryParams.toString()}`;
};

const NotableParties = () => {
  const {
    data: { occupants },
    isLoading,
  } = useOccupantsQuery({ omitStatuses: ['canceled'] });

  const meetsNotableCriteria = (occupant: ServiceOccupant) => {
    return (
      isReservation(occupant) &&
      (occupant.isVIP ||
        occupant.hasDietaryRestrictionOrAllergy ||
        Boolean(occupant.note))
    );
  };

  const notableOccupants: ServiceReservation[] = (occupants || []).filter(
    (occupant) => meetsNotableCriteria(occupant),
  ) as ServiceReservation[];

  const hasNotableOccupants = notableOccupants.length > 0;

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!hasNotableOccupants && !isLoading) {
    return <span>No notable parties today</span>;
  }

  return (
    <ul aria-label="Notable Parties">
      {notableOccupants.map((occupant) => {
        const guestName = `${occupant.guest.firstName} ${occupant.guest.lastName}`;
        const formattedReservationTime = ISOTimeTo12HourTime(occupant.time);
        const hasGuestTags = occupant.guestTags.length > 0;
        const hasNoteToRestaurant = Boolean(occupant.note);

        return (
          <li
            key={occupant.id}
            className={styles.occupantRow}
            aria-label={guestName}
          >
            <div className={styles.occupantDetailsRow}>
              <span className={cx(typography.c3, styles.occupantTime)}>
                {formattedReservationTime}
              </span>
              <Link
                to={occupantDetailsHref(occupant.id)}
                aria-label={`View details for ${guestName}'s reservation`}
                className={styles.occupantName}
              >
                <span>
                  <strong>{guestName}</strong>
                </span>
              </Link>
              <div className={styles.occupantGuestCount}>
                <Icon name="couple" className={styles.icon} />
                <span className={typography.c3}>{occupant.guestCount}</span>
              </div>
            </div>
            <div className={styles.notableCharacteristics}>
              {hasGuestTags && (
                <div className={styles.guestTags}>
                  {occupant.guestTags.map((tag) => (
                    <GuestTagPill
                      key={tag.id}
                      title={tag.title}
                      category={tag.category}
                      className={styles.guestTag}
                    />
                  ))}
                </div>
              )}
              {hasNoteToRestaurant && (
                <span className={cx(typography.t1, styles.noteToRestaurant)}>
                  {occupant.note}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

interface RestaurantNoteForm {
  dayNote: string | null;
}

const RestaurantNotes = ({ date }: { date: Date }) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<RestaurantNoteForm>({
    defaultValues: {
      dayNote: null,
    },
  });
  const { id: restaurantId } = useRestaurant();

  const { isPending, error } = useQuery<RestaurantDayNote | null>({
    queryKey: ['restaurants', restaurantId, 'dayNotes', date],
    queryFn: async () => {
      const data = await getRestaurantNote({
        restaurantId,
        date: toISODateFormat(date),
      });

      setValue('dayNote', data?.note || null);

      return data;
    },
  });

  const onSubmit = async (data: RestaurantNoteForm) => {
    const formattedDate = toISODateFormat(date);
    const trimmedDayNote = data.dayNote?.trim();

    if (!trimmedDayNote) {
      try {
        await deleteRestaurantNote({
          date: formattedDate,
          restaurantId,
        });
        successToast({ message: 'Note deleted.' });
      } catch (error) {
        errorToast({ message: 'Error deleting note. Please try again.' });
        reportAppError(error);
      }
    } else {
      try {
        await saveRestaurantNote({
          note: trimmedDayNote,
          date: formattedDate,
          restaurantId,
        });
        successToast({ message: 'Note saved.' });
      } catch (error) {
        errorToast({ message: 'Error saving note. Please try again.' });
        reportAppError(error);
      }
    }
  };

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return <span>There was an error loading the note. Please try again.</span>;
  }

  return (
    <form
      aria-labelledby="dialogTitle"
      onSubmit={handleSubmit(onSubmit)}
      className={styles.form}
    >
      <span className={cx(typography.c2, styles.warning)}>
        All restaurant staff will be able to view and edit this note.
      </span>
      <ControlledFormTextareaAutosize
        control={control}
        name="dayNote"
        label=""
        placeholder="Type the day note here..."
        ariaLabel="Create or update the restaurant note"
        minRows={3}
        maxRows={3}
      />
      <Button
        label="Save"
        type="submit"
        variant={ButtonVariants.Primary}
        isDisabled={isSubmitting}
        className={styles.submitButton}
      />
    </form>
  );
};
